/**
 * CopySelectScreen - Copy Existing Project for FMEA Creation Wizard
 * Displays user's existing projects for selection and duplication
 */

import React, { useState } from 'react';
import type { Project } from '@fmeastudio/shared';
import { useProjects } from '../../hooks/useProjects';
import { useWizard } from '../../contexts/WizardContext';
import { ProjectPreviewModal } from './ProjectPreviewModal';
import './CopySelectScreen.css';

export function CopySelectScreen() {
  const { goToStep } = useWizard();
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [newName, setNewName] = useState('');

  // Fetch active projects only (exclude templates and archived)
  const { data: projects, isLoading, isError, error } = useProjects(false);

  const handleProjectSelect = (project: Project) => {
    setSelectedProject(project);
    setNewName(`${project.name} - Copy`);
    setShowPreview(true);
  };

  const handleGoBack = () => {
    goToStep('welcome');
  };

  // Filter out templates - show only ACTIVE user projects
  const activeProjects = projects?.filter((p) => p.status === 'ACTIVE') || [];

  // Loading state
  if (isLoading) {
    return (
      <div className="copy-select-screen" data-testid="wizard-copy-select">
        <div className="copy-loading">
          <div className="spinner" />
          <p>Loading your projects...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (isError) {
    return (
      <div className="copy-select-screen" data-testid="wizard-copy-select">
        <div className="copy-error">
          <h2>Error Loading Projects</h2>
          <p>{(error as Error)?.message || 'Failed to load projects'}</p>
          <button onClick={handleGoBack} className="btn-secondary">
            Go Back
          </button>
        </div>
      </div>
    );
  }

  // Empty state - no projects to copy
  if (activeProjects.length === 0) {
    return (
      <div className="copy-select-screen" data-testid="wizard-copy-select">
        <div className="copy-empty">
          <h2>No Projects Available</h2>
          <p>You don't have any existing projects to copy yet.</p>
          <p>Try creating a project from a template or from scratch instead.</p>
          <button onClick={handleGoBack} className="btn-secondary">
            Go Back
          </button>
        </div>
      </div>
    );
  }

  // Project list
  return (
    <div className="copy-select-screen" data-testid="wizard-copy-select">
      <div className="copy-header">
        <button onClick={handleGoBack} className="btn-back" aria-label="Go back">
          ← Back
        </button>
        <div className="copy-title">
          <h2>Copy Existing Project</h2>
          <p>Select a project to duplicate with all its components and failure modes</p>
        </div>
      </div>

      <div className="copy-list">
        {activeProjects.map((project) => (
          <div
            key={project.id}
            className="copy-project-card"
            onClick={() => handleProjectSelect(project)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleProjectSelect(project);
              }
            }}
          >
            <div className="copy-project-header">
              <h3>{project.name}</h3>
              {project.industry && (
                <span className="copy-project-industry">{project.industry}</span>
              )}
            </div>
            {project.description && (
              <p className="copy-project-description">{project.description}</p>
            )}
            <div className="copy-project-footer">
              <span className="copy-project-date">
                Created {new Date(project.createdAt).toLocaleDateString()}
              </span>
              <button className="btn-preview">Preview →</button>
            </div>
          </div>
        ))}
      </div>

      {/* Project preview modal */}
      {showPreview && selectedProject && (
        <ProjectPreviewModal
          project={selectedProject}
          defaultName={newName}
          onCancel={() => {
            setShowPreview(false);
            setSelectedProject(null);
          }}
        />
      )}
    </div>
  );
}
